/** Normalize country labels to match user app / API (United States, not USA). */
export function normalizeCountry(country) {
  const c = String(country || "").trim();
  if (!c) return "";
  const upper = c.toUpperCase();
  if (upper === "USA" || upper === "US" || c === "U.S." || c === "U.S.A.") {
    return "United States";
  }
  return c;
}

/**
 * Flatten sector-industry API response to unique { industry, sector, country } tuples.
 * Dedupes on industry::sector::country.
 */
export function flattenIndustryIndex(sectors) {
  if (!Array.isArray(sectors)) return [];
  const seen = new Set();
  const tuples = [];

  for (const sector of sectors) {
    const sectorName = sector?.name;
    if (!sectorName) continue;
    const countries = Array.isArray(sector.countries) ? sector.countries : [];
    for (const rawCountry of countries) {
      const country = normalizeCountry(rawCountry);
      const byCountry = sector.industries || {};
      const industries =
        byCountry[rawCountry] ||
        byCountry[country] ||
        [];
      for (const industry of industries) {
        if (!industry) continue;
        const key = `${industry}::${sectorName}::${country}`;
        if (seen.has(key)) continue;
        seen.add(key);
        tuples.push({ industry, sector: sectorName, country });
      }
    }
  }

  return tuples;
}

function matchScore(haystack, query) {
  const h = haystack.toLowerCase();
  const q = query.toLowerCase();
  if (!h.includes(q)) return 0;
  if (h === q) return 100;
  if (h.startsWith(q)) return 80;
  return 40;
}

/**
 * Client-side search/rank: industry weight highest, then sector, then country.
 */
export function filterAndPrioritizeIndustries(tuples, searchText, limit = 30) {
  const list = Array.isArray(tuples) ? tuples : [];
  const q = String(searchText || "").trim();
  if (!q) return list.slice(0, limit);

  const scored = list
    .map((tuple) => {
      const industryScore = matchScore(tuple.industry, q) * 3;
      const sectorScore = matchScore(tuple.sector, q) * 2;
      const countryScore = matchScore(tuple.country, q);
      const score = industryScore + sectorScore + countryScore;
      return { ...tuple, score };
    })
    .filter((t) => t.score > 0);

  scored.sort((a, b) => b.score - a.score || a.industry.localeCompare(b.industry));
  return scored.slice(0, limit);
}

export function sectorsForCountry(sectors, country) {
  const norm = normalizeCountry(country);
  if (!norm || !Array.isArray(sectors)) return [];
  return sectors.filter((s) =>
    (s.countries || []).some((c) => normalizeCountry(c) === norm)
  );
}

export function industriesForSectorCountry(sectors, sectorName, country) {
  const norm = normalizeCountry(country);
  const sector = (sectors || []).find((s) => s.name === sectorName);
  if (!sector || !norm) return [];
  const rawCountry = (sector.countries || []).find((c) => normalizeCountry(c) === norm);
  const byCountry = sector.industries || {};
  return byCountry[rawCountry] || byCountry[norm] || [];
}

/** Resolve sector/country from saved CATEGORIES[0] + stock_country. */
export function resolveIndustrySelection(metadata, flattenedIndex, sectors) {
  const industryLabel = metadata?.CATEGORIES?.[0];
  const country = normalizeCountry(metadata?.stock_country || "") || "India";
  if (!industryLabel) {
    return { country, sector: "", industry: "" };
  }

  const match =
    flattenedIndex.find(
      (t) =>
        t.industry === industryLabel &&
        (!metadata?.stock_country || t.country === country)
    ) || flattenedIndex.find((t) => t.industry === industryLabel);

  if (match) {
    return {
      country: match.country,
      sector: match.sector,
      industry: match.industry,
    };
  }

  for (const sector of sectorsForCountry(sectors, country)) {
    const industries = industriesForSectorCountry(sectors, sector.name, country);
    if (industries.includes(industryLabel)) {
      return { country, sector: sector.name, industry: industryLabel };
    }
  }

  return { country, sector: "", industry: industryLabel };
}
